import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MoreVertical, X, ChevronDown, Instagram } from 'lucide-react';
import { InstagramAccount } from '../../types';
import { translations } from '../../translations';
import { getPlanBadgeStyles } from '../../utils/planStyles';
import { imageService } from '../../services/imageService';
import { supabase } from '../../lib/supabase';
import { AddAccountModal } from './AddAccountModal';

interface AccountListProps {
  accounts: InstagramAccount[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedAccount: InstagramAccount | null;
  setSelectedAccount: (account: InstagramAccount) => void;
  setSelectedTab: (tab: string) => void;
  isDarkMode: boolean;
  language: string;
  onShowAddModal: () => void;
}

const PLACEHOLDER_URL = 'https://via.placeholder.com/150';

export function AccountList({ 
  accounts, 
  searchQuery, 
  setSearchQuery, 
  selectedAccount, 
  setSelectedAccount,
  setSelectedTab,
  isDarkMode,
  language,
  onShowAddModal
}: AccountListProps) {
  const navigate = useNavigate();
  const t = translations[language as keyof typeof translations];
  const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isPlanDropdownOpen, setIsPlanDropdownOpen] = useState(false);
  const [refreshLimits, setRefreshLimits] = useState<Record<string, any>>({});
  const [visibilityStates, setVisibilityStates] = useState<Record<string, boolean>>({});
  
  useEffect(() => {
    const checkAdminStatus = async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      setIsAdmin(profile?.role === 'admin');
    };

    checkAdminStatus();
  }, []);

  const loadRefreshLimits = useCallback(async () => {
    if (!accounts.length) return;
    
    try {
      // Get session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // For each account, check refresh limits
      const limitsMap: Record<string, any> = {};
      for (const account of accounts) {
        try {
          const { data, error } = await supabase.rpc('check_refresh_limit', {
            p_user_id: session.user.id,
            p_account_id: account.id
          });
          
          if (!error && data) {
            limitsMap[account.id] = data;
          }
        } catch (err) {
          console.error(`Error checking limits for account ${account.id}:`, err);
        }
      }

      setRefreshLimits(limitsMap);
    } catch (error) {
      console.error('Error loading refresh limits:', error);
    }
  }, [accounts]);

  useEffect(() => {
    loadRefreshLimits();
  }, [accounts, loadRefreshLimits]);

  // Initialize visibility states
  useEffect(() => {
    const initialStates: Record<string, boolean> = {};
    accounts.forEach(account => {
      initialStates[account.id] = account.visible_to_closers || false;
    });
    setVisibilityStates(initialStates);
  }, [accounts]);

  const handleVisibilityToggle = async (accountId: string, currentVisibility: boolean) => {
    try {
      const newVisibility = !currentVisibility;
      
      // Update in database
      const { error } = await supabase
        .from('instagram_accounts')
        .update({ visible_to_closers: newVisibility })
        .eq('id', accountId);

      if (error) throw error;

      // Update local state
      setVisibilityStates(prev => ({
        ...prev,
        [accountId]: newVisibility
      }));
    } catch (error) {
      console.error('Error updating visibility:', error);
    }
  };

  const filteredAccounts = accounts.filter(account => {
    // Filter by search query
    const matchesSearch = (
      account.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      account.username.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Filter by selected statuses
    const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(account.connection_status || '');

    // Filter by selected plans
    const matchesPlan = selectedPlans.length === 0 || selectedPlans.includes(account.plan || '');

    return matchesSearch && matchesStatus && matchesPlan;
  });

  const handleImageError = async (e: React.SyntheticEvent<HTMLImageElement>, account: InstagramAccount) => {
    try {
      const target = e.target as HTMLImageElement;
      const originalSrc = target.src;

      if (originalSrc === PLACEHOLDER_URL || loadingImages[account.username]) {
        return;
      }

      setLoadingImages(prev => ({ ...prev, [account.username]: true }));

      if (account.profile_pic_url_hd) {
        const newUrl = await imageService.handleImageError(account.profile_pic_url_hd, account.username);
        if (newUrl && newUrl !== PLACEHOLDER_URL) {
          target.src = newUrl;

          const { error: updateError } = await supabase
            .from('instagram_accounts')
            .update({ 
              avatar: newUrl,
              profile_pic_url_hd: newUrl 
            })
            .eq('id', account.id);

          if (updateError) {
            console.error('Failed to update account avatar:', updateError);
          }
          setLoadingImages(prev => ({ ...prev, [account.username]: false }));
          return;
        }
      }

      const newUrl = await imageService.handleImageError(originalSrc, account.username);
      if (newUrl && newUrl !== PLACEHOLDER_URL) {
        target.src = newUrl;

        const { error: updateError } = await supabase
          .from('instagram_accounts')
          .update({ avatar: newUrl })
          .eq('id', account.id);

        if (updateError) {
          console.error('Failed to update account avatar:', updateError);
        }
      }
    } catch (error) {
      console.error('Failed to handle image error:', error);
    } finally {
      setLoadingImages(prev => ({ ...prev, [account.username]: false }));
    }
  };

  const getImageKey = (account: InstagramAccount) => {
    return `${account.id}-${account.avatar || 'placeholder'}-${Date.now()}`;
  };

  const getConnectionStatusColor = (status: string | null) => {
    switch (status) {
      case 'connected':
        return 'text-emerald-500';
      case '2fa':
        return 'text-amber-500';
      case 'failed':
        return 'text-red-500';
      default:
        return isDarkMode ? 'text-gray-400' : 'text-gray-500';
    }
  };

  const getConnectionStatusText = (status: string | null) => {
    switch (status) {
      case 'connected':
        return t.connection_status_connected;
      case '2fa':
        return t.connection_status_2fa;
      case 'failed':
        return t.connection_status_failed;
      case 'pending':
        return t.connection_status_pending;
      default:
        return '';
    }
  };

  const resetFilters = () => {
    setSelectedStatuses([]);
    setSelectedPlans([]);
    setIsStatusDropdownOpen(false);
    setIsPlanDropdownOpen(false);
  };

  const toggleStatus = (status: string) => {
    setSelectedStatuses(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const togglePlan = (plan: string) => {
    setSelectedPlans(prev => 
      prev.includes(plan) 
        ? prev.filter(p => p !== plan)
        : [...prev, plan]
    );
  };

  return (
    <div className={`w-72 border-r h-screen overflow-hidden flex flex-col ${
      isDarkMode 
        ? 'bg-[#121212] border-[#2b2b2b]' 
        : 'bg-white border-[#e5e7eb]'
    }`}>
      <div className="p-3 space-y-3 pt-20 lg:pt-3">
        <div className="flex items-center gap-2">
          <Instagram size={20} className={isDarkMode ? 'text-gray-200' : 'text-gray-900'} strokeWidth={2} />
          <h1 className={`text-lg font-semibold ${
          isDarkMode ? 'text-gray-200' : 'text-gray-900'
          }`}>
          Instagram
          </h1>
        </div>
        
        <button
          onClick={onShowAddModal}
          className="w-full bg-orange-500 text-white rounded-lg py-2 flex items-center justify-center gap-1.5 hover:bg-orange-600 transition-colors text-[13px] font-medium"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14"></path>
            <path d="M12 5v14"></path>
          </svg>
          <span>{t.add_account}</span>
        </button>
        
        <div className="relative">
          <input
            type="text"
            placeholder={t.search_accounts}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full rounded-lg pl-9 pr-4 py-2 text-[13px] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-500 ${
              isDarkMode 
                ? 'bg-[#2b2b2b] text-gray-200' 
                : 'bg-gray-50 text-gray-900'
            }`}
          />
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" strokeWidth={2.3} />
        </div>

        {isAdmin && (
          <div className="flex items-center gap-2">
            {(selectedStatuses.length > 0 || selectedPlans.length > 0) && (
              <button
                onClick={resetFilters}
                className={`p-1.5 rounded-lg transition-colors ${
                  isDarkMode
                    ? 'hover:bg-[#2b2b2b] text-gray-400'
                    : 'hover:bg-gray-100 text-gray-500'
                }`}
              >
                <X size={14} strokeWidth={2.5} />
              </button>
            )}

            <div className="relative flex-1">
              <button
                onClick={() => {
                  setIsStatusDropdownOpen(!isStatusDropdownOpen);
                  setIsPlanDropdownOpen(false);
                }}
                className={`w-full px-3 py-1.5 text-[13px] rounded-lg flex items-center justify-between ${
                  isDarkMode 
                    ? 'bg-[#2b2b2b] text-gray-200 hover:bg-[#333333]' 
                    : 'bg-gray-50 text-gray-900 hover:bg-gray-100'
                }`}
              >
                <span className="truncate">
                  {selectedStatuses.length > 0 
                    ? `${selectedStatuses.length} status${selectedStatuses.length > 1 ? 'es' : ''}`
                    : 'Status'}
                </span>
                <ChevronDown size={14} className={`transition-transform ${isStatusDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isStatusDropdownOpen && (
                <div className={`absolute top-full left-0 right-0 mt-1 p-2 rounded-lg shadow-lg border z-10 ${
                  isDarkMode 
                    ? 'bg-[#2b2b2b] border-[#3b3b3b]' 
                    : 'bg-white border-gray-200'
                }`}>
                  <div className="flex flex-wrap gap-1.5">
                    {['connected', '2fa', 'failed', 'pending'].map(status => (
                      <button
                        key={status}
                        onClick={() => toggleStatus(status)}
                        className={`px-2 py-1 rounded-full text-[12px] font-medium transition-colors ${
                          selectedStatuses.includes(status)
                            ? getConnectionStatusColor(status) + ' bg-current/10'
                            : isDarkMode
                              ? 'text-gray-400 bg-[#333333] hover:text-gray-300'
                              : 'text-gray-500 bg-gray-100 hover:text-gray-700'
                        }`}
                      >
                        <span className="relative z-[1]">
                          {getConnectionStatusText(status)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="relative flex-1">
              <button
                onClick={() => {
                  setIsPlanDropdownOpen(!isPlanDropdownOpen);
                  setIsStatusDropdownOpen(false);
                }}
                className={`w-full px-3 py-1.5 text-[13px] rounded-lg flex items-center justify-between ${
                  isDarkMode 
                    ? 'bg-[#2b2b2b] text-gray-200 hover:bg-[#333333]' 
                    : 'bg-gray-50 text-gray-900 hover:bg-gray-100'
                }`}
              >
                <span className="truncate">
                  {selectedPlans.length > 0 
                    ? `${selectedPlans.length} plan${selectedPlans.length > 1 ? 's' : ''}`
                    : 'Plan'}
                </span>
                <ChevronDown size={14} className={`transition-transform ${isPlanDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isPlanDropdownOpen && (
                <div className={`absolute top-full left-0 right-0 mt-1 p-2 rounded-lg shadow-lg border z-10 ${
                  isDarkMode 
                    ? 'bg-[#2b2b2b] border-[#3b3b3b]' 
                    : 'bg-white border-gray-200'
                }`}>
                  <div className="flex flex-wrap gap-1.5">
                    {['Amplr Start', 'Amplr Max', 'Amplr Pro'].map(plan => (
                      <button
                        key={plan}
                        onClick={() => togglePlan(plan)}
                        className={`px-2 py-1 rounded-full text-[12px] font-medium transition-colors ${
                          selectedPlans.includes(plan)
                            ? getPlanBadgeStyles(plan)
                            : isDarkMode
                              ? 'text-gray-400 bg-[#333333] hover:text-gray-300'
                              : 'text-gray-500 bg-gray-100 hover:text-gray-700'
                        }`}
                      >
                        <span className="relative z-[1]">
                          {plan.replace('Amplr ', '')}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="px-3 space-y-2 pb-4">
          {filteredAccounts.map(account => (
            <button
              key={account.id}
              onClick={() => {
                setSelectedAccount(account);
                setSelectedTab('overview');
              }}
              className={`w-full p-2 rounded-lg transition-colors flex items-center gap-3 group ${
                selectedAccount?.id === account.id 
                  ? isDarkMode ? 'bg-[#1c1c1c]' : 'bg-gray-50'
                  : ''
              } ${
                isDarkMode 
                  ? 'hover:bg-[#1c1c1c]' 
                  : 'hover:bg-gray-50'
              }`}
            >
              <div className="w-10 h-10 rounded-full bg-gray-100 overflow-hidden flex-shrink-0">
                <img
                  key={getImageKey(account)}
                  src={account.profile_pic_url_hd || account.avatar || PLACEHOLDER_URL}
                  alt={account.name}
                  className={`w-full h-full object-cover transition-opacity duration-200 ${
                    loadingImages[account.username] ? 'opacity-50' : 'opacity-100'
                  }`}
                  onError={(e) => handleImageError(e, account)}
                  loading="lazy"
                  crossOrigin="anonymous"
                />
              </div>
              <div className="flex-1 text-left">
                <div className={`text-[13px] font-medium ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-900'
                }`}>
                  {account.name}
                  {account.plan && (
                    <span className={`ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${getPlanBadgeStyles(account.plan)}`}>
                      {account.plan.replace('Amplr ', '')}
                    </span>
                  )}
                </div>
                <div className={`text-[12px] ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {account.username}
                </div>
                {account.connection_status && (
                  <div className={`text-[11px] font-medium ${getConnectionStatusColor(account.connection_status)}`}>
                    {getConnectionStatusText(account.connection_status)}
                  </div>
                )}
              </div>
              <MoreVertical 
                size={15} 
                className={`opacity-0 group-hover:opacity-100 transition-opacity ${
                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                }`} 
                strokeWidth={2.3} 
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}